/**
 * daysheetLayout.ts — layout model for the Bandz daysheet PDF generator.
 *
 * The daysheet PDF (buildDaysheetDoc in src/lib/daysheet.ts) is assembled from
 * a list of named sections. This module defines that model so an admin UI can
 * reorder / toggle sections and edit fixed texts & style, without touching the
 * generator code — mirroring the rider layout system (src/lib/riderLayout.ts).
 *
 * - createDefaultDaysheetLayout() reproduces the EXACT hard-coded behaviour of
 *   the original generator (all sections enabled, current order, no footer).
 * - mergeDaysheetLayouts() layers a persisted (partial) admin override on top
 *   of the defaults: texts/style are shallow-merged per key, the sections
 *   array wins WHOLESALE (it is the full ordered list).
 *
 * Layouts are persisted in the same `pdf_layouts` table as the rider, under a
 * distinct scope_key prefix ('daysheet:global' + 'daysheet:<bandUuid>').
 */

/** All sections the daysheet PDF can emit, in the current render order. */
export type DaysheetSectionId =
  | 'header'
  /** Axe du voyage (FROM → TO → moyen) : n'a de contenu que sur un travel day. */
  | 'travel'
  | 'general-info'
  | 'venue'
  | 'contacts'
  | 'hotel'
  | 'rooming'
  | 'run-of-show'
  | 'guestlist'
  | 'flights'
  | 'distances'
  | 'notes'
  | 'tech-notes'
  | 'misc-notes'
  | 'categorized-notes'
  | 'band-crew'
  | 'venue-photo';


/** One section of the daysheet: enabled flag + optional title override. */
export interface DaysheetLayoutSection {
  id: DaysheetSectionId;
  enabled: boolean;
  /** When a non-empty string, replaces the default heading / subtitle text. */
  title?: string | null;
  /** Start the section on a new page. */
  pageBreakBefore?: boolean;
  /**
   * Ordered list of the section's renderable fields. When absent the section
   * renders ALL its fields in the engine's default order (back-compat with
   * layouts saved before field-level editing existed). When present, only
   * enabled fields render, in list order — how the editor places each field.
   */
  fields?: LayoutField[];
  /**
   * Largeurs ÉDITABLES des colonnes du tableau de la section, par id de colonne
   * (poids relatif, 1 = colonne étroite). Absent = les largeurs par défaut du
   * moteur (`DAYSHEET_TABLE_COLUMNS`), donc aucune migration : un layout
   * enregistré avant cette option rend exactement comme avant.
   *
   * Les poids sont normalisés en pourcentages au rendu : la somme fait toujours
   * 100 % de la largeur utile, aucune colonne ne peut faire déborder la page.
   */
  columnWidths?: Record<string, number> | null;
}

/**
 * A single renderable field inside a section. Two special kinds:
 *  - id 'spacer' (or '…\.spacer') → a vertical gap of `size` pdfmake units
 *  - id 'text' (or '…\.text')     → a free-text paragraph (`textContent`,
 *    `bold`, `italic`, `fontSize`, `align`)
 * All other ids map to the section's engine-generated content.
 */
export interface LayoutField {
  /** Stable engine id, e.g. 'general-info.show-duration', 'spacer', 'text'. */
  id: string;
  enabled: boolean;
  /** Label shown in the editor (null = engine default label). */
  label?: string | null;
  /** Spacer fields only: vertical gap height in pdfmake units (default 8). */
  size?: number;
  /** Free-text fields only (id 'text'): the paragraph content. */
  textContent?: string;
  /** Free-text fields only: bold paragraph. */
  bold?: boolean;
  /** Free-text fields only: italic paragraph. */
  italic?: boolean;
  /** Free-text fields only: font size override. */
  fontSize?: number;
  /** Free-text fields only: horizontal alignment. */
  align?: 'left' | 'center' | 'right';
  /**
   * TABLE-COLUMN fields only (id `<section>.<columnId>`, ex. `contacts.role`) :
   * POIDS de la colonne, normalisé en % au rendu. Absent = défaut de
   * `DAYSHEET_TABLE_COLUMNS` / `TOURBOOK_TABLE_COLUMNS`.
   */
  width?: number;
}
export type { LayoutField as DaysheetLayoutField };

/** Fixed texts of the daysheet. {token} placeholders are filled at render time. */
export interface DaysheetLayoutTexts {
  /** Appended to the band name for the document title line, e.g. 'DAYSHEET'. */
  titleSuffix: string;
  /** Footer left; {bandName} placeholder. */
  footerLeftTemplate: string;
  /** Footer center; {venueName} placeholder. */
  footerCenterTemplate: string;
  /** Footer right; {page} and {pages} placeholders. */
  footerRightTemplate: string;
  /** When true (default), the footer is printed at the bottom of EVERY page.
   * Optional for back-compat: absent = enabled. Note the footer only renders
   * when at least one of the three templates is filled in. */
  footerEnabled?: boolean;
  /** When true, a running header (like the footer) is printed at the top of
   * EVERY page. Off by default so the original look is unchanged. */
  headerEnabled: boolean;
  /** Running header left; {bandName} placeholder. */
  headerLeftTemplate: string;
  /** Running header center; {venueName} placeholder. */
  headerCenterTemplate: string;
  /** Running header right; {page} and {pages} placeholders. */
  headerRightTemplate: string;
}

/** Visual style of the daysheet PDF. Values match the original hard-coded generator. */
export interface DaysheetLayoutStyle {
  titleFontSize: number;
  titleColor: string;
  subtitleFontSize: number;
  subtitleColor: string;
  headingFontSize: number;
  headingColor: string;
  /** Label size/color of the GENERAL INFORMATION label:value grid. */
  labelFontSize: number;
  labelColor: string;
  bodyFontSize: number;
  textColor: string;
  footerFontSize: number;
  footerColor: string;
  pageMargins: [number, number, number, number];
}

/** Complete persisted layout for the daysheet PDF generator. */
export interface DaysheetLayout {
  version: 1;
  sections: DaysheetLayoutSection[];
  texts: DaysheetLayoutTexts;
  style: DaysheetLayoutStyle;
}

/**
 * Colonnes des TABLEAUX du daysheet : id stable, libellé i18n (clé `pdf.ds.col.*`
 * ou clé dédiée) et POIDS par défaut. Le poids reproduit exactement les largeurs
 * historiques du moteur (un `'*'` vaut « l'espace restant », d'où son poids).
 * ⚠️ Les commentaires de `buildXxx()` dans `daysheet.ts` doivent rester alignés.
 */
export interface DaysheetTableColumn {
  /** Stable id persisted in `section.columnWidths`. NEVER rename (layouts in DB). */
  id: string;
  /** i18n key of the header label, reused by the editor. */
  labelKey: string;
  /** Default weight (normalised to % at render time). */
  weight: number;
}

export const DAYSHEET_TABLE_COLUMNS: Partial<Record<DaysheetSectionId, DaysheetTableColumn[]>> = {
  // Nouveau tableau (2026-11-03) : From | To | Means. Pas d'historique à respecter,
  // on part de poids égaux-ish (le moyen de transport est court).
  travel: [
    { id: 'from', labelKey: 'pdf.ds.col.from', weight: 38 },
    { id: 'to', labelKey: 'pdf.ds.col.to', weight: 38 },
    { id: 'means', labelKey: 'pdf.ds.col.means', weight: 24 },
  ],
  // anciennes largeurs : ['*', '30%']
  'band-crew': [
    { id: 'name', labelKey: 'pdf.ds.col.name', weight: 70 },
    { id: 'position', labelKey: 'pdf.ds.col.position', weight: 30 },
  ],
  // ['25%', '30%', '25%', '*']
  contacts: [
    { id: 'role', labelKey: 'pdf.ds.col.role', weight: 25 },
    { id: 'name', labelKey: 'pdf.ds.col.name', weight: 30 },
    { id: 'phone', labelKey: 'pdf.ds.col.phone', weight: 25 },
    { id: 'email', labelKey: 'pdf.ds.col.email', weight: 20 },
  ],
  // ['30%', '*', '20%', '15%']
  hotel: [
    { id: 'hotel', labelKey: 'pdf.ds.col.hotel', weight: 30 },
    { id: 'address', labelKey: 'pdf.ds.col.address', weight: 35 },
    { id: 'phone', labelKey: 'pdf.ds.col.phone', weight: 20 },
    { id: 'conf', labelKey: 'pdf.ds.col.conf', weight: 15 },
  ],
  // ['*', '15%', '20%']
  rooming: [
    { id: 'name', labelKey: 'pdf.ds.col.name', weight: 65 },
    { id: 'room', labelKey: 'pdf.ds.col.room', weight: 15 },
    { id: 'hotel', labelKey: 'pdf.ds.col.hotel', weight: 20 },
  ],
  // ['12%', '*', '20%']
  'run-of-show': [
    { id: 'time', labelKey: 'pdf.ds.col.time', weight: 12 },
    { id: 'activity', labelKey: 'pdf.ds.col.activity', weight: 68 },
    { id: 'responsible', labelKey: 'pdf.ds.col.responsible', weight: 20 },
  ],
  // ['40%', '30%', '30%']
  guestlist: [
    { id: 'name', labelKey: 'pdf.ds.col.name', weight: 40 },
    { id: 'role', labelKey: 'pdf.ds.col.role', weight: 30 },
    { id: 'status', labelKey: 'pdf.ds.col.status', weight: 30 },
  ],
  // ['32%', '13%', '13%', '22%', '*']
  flights: [
    { id: 'people', labelKey: 'pdf.ds.col.people', weight: 32 },
    { id: 'from', labelKey: 'pdf.ds.col.from', weight: 13 },
    { id: 'to', labelKey: 'pdf.ds.col.to', weight: 13 },
    { id: 'flight', labelKey: 'pdf.ds.col.flight', weight: 22 },
    { id: 'timeDuration', labelKey: 'pdf.ds.col.timeDuration', weight: 20 },
  ],
  // ['26%', '26%', '14%', '18%', '*']
  distances: [
    { id: 'from', labelKey: 'pdf.ds.col.from', weight: 26 },
    { id: 'to', labelKey: 'pdf.ds.col.to', weight: 26 },
    { id: 'mode', labelKey: 'pdf.ds.col.mode', weight: 14 },
    { id: 'distance', labelKey: 'pdf.ds.col.distance', weight: 18 },
    { id: 'duration', labelKey: 'pdf.ds.col.duration', weight: 16 },
  ],
};

/** Champs par défaut des sections TABLEAU : une entrée par colonne. */
export function daysheetTableColumnFields(sectionId: DaysheetSectionId): LayoutField[] {
  return (DAYSHEET_TABLE_COLUMNS[sectionId] || []).map((c) => ({
    id: `${sectionId}.${c.id}`,
    enabled: true,
    label: null,
    width: c.weight,
  }));
}

/**
 * Colonnes RETENUES pour une section tableau, dans l'ordre du layout.
 * - champs du layout contenant au moins un id de colonne connu → on suit CET ordre
 *   et on ne garde que les colonnes actives ;
 * - liste legacy (aucun id connu : la section n'avait qu'un `<section>.table`) →
 *   TOUTES les colonnes, ordre par défaut (aucune colonne ne disparaît toute seule) ;
 * - toutes les colonnes décochées → [] (tableau vide VOULU, on ne ressuscite rien).
 */
export function daysheetTableColumns(
  section: DaysheetLayoutSection | undefined,
  sectionId: DaysheetSectionId,
): { id: string; label: string; weight: number; labelOverride: string | null }[] {
  const defs = DAYSHEET_TABLE_COLUMNS[sectionId] || [];
  if (!defs.length) return [];
  const byId = new Map(defs.map((d) => [d.id, d]));
  const saved = (section?.fields ?? []).filter((f) => byId.has(f.id.slice(sectionId.length + 1)));
  const list: LayoutField[] = saved.length
    ? saved
    : defs.map((d) => ({ id: `${sectionId}.${d.id}`, enabled: true, label: null, width: d.weight }));
  return list
    .filter((f) => f.enabled !== false)
    .map((f) => {
      const cid = f.id.slice(sectionId.length + 1);
      const def = byId.get(cid)!;
      const w = typeof f.width === 'number' && f.width > 0 ? f.width : def.weight;
      const override = typeof f.label === 'string' && f.label.trim() ? f.label.trim() : null;
      return { id: cid, label: override || def.labelKey, weight: w, labelOverride: override };
    });
}

/** Poids effectifs d'une section : surcharge du layout sinon défauts. */
export function daysheetColumnWeights(
  sectionId: DaysheetSectionId,
  override?: Record<string, number> | null,
): number[] {
  const cols = DAYSHEET_TABLE_COLUMNS[sectionId] || [];
  return cols.map((c) => {
    const v = override?.[c.id];
    return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : c.weight;
  });
}

/**
 * `widths[]` pdfmake d'une section : les poids normalisés en pourcentages.
 * Une seule source de vérité — le moteur ne code plus aucune largeur en dur.
 */
export function daysheetTableWidths(
  sectionId: DaysheetSectionId,
  override?: Record<string, number> | null,
): string[] {
  const weights = daysheetColumnWeights(sectionId, override);
  // (les colonnes réellement imprimées passent par `daysheetTableColumns`)
  const total = weights.reduce((a, b) => a + b, 0) || 1;
  return weights.map((w) => `${((w / total) * 100).toFixed(2)}%`);
}

/** Section ids in the current hard-coded render order (default layout). */
export const DEFAULT_SECTION_ORDER: DaysheetSectionId[] = [
  'header',
  // Le voyage se lit AVANT le reste : un travel day n'a pas de salle, sa première
  // information utile est l'axe FROM → TO et le moyen de transport (2026-11-03).
  'travel',
  'general-info',
  'venue',
  'venue-photo',
  'band-crew',
  'contacts',
  'hotel',
  'rooming',
  'run-of-show',
  'guestlist',
  'flights',
  'distances',
  'notes',
  'tech-notes',
  'misc-notes',
  'categorized-notes',
];

/** Friendly editor names for each section (shown as input placeholders). */
export const DEFAULT_SECTION_TITLES: Record<DaysheetSectionId, string> = {
  header: 'Header',
  travel: 'Travel',
  'general-info': 'General Information',
  venue: 'Venue',
  'venue-photo': 'Venue photo',
  'band-crew': 'Band & Crew',
  contacts: 'Contact Persons',
  hotel: 'Hotel',
  rooming: 'Rooming List',
  'run-of-show': 'Timings',
  guestlist: 'Guestlist',
  flights: 'Flights & Transfers',
  distances: 'Distances',
  notes: 'Notes',
  'tech-notes': 'Tech Notes',
  'misc-notes': 'Logistics Notes',
  'categorized-notes': 'Categorized Notes',
};

/** What each section renders in the daysheet PDF — shown in the editor so it
 * mirrors the generated document. */
export const SECTION_DETAILS: Record<DaysheetSectionId, string[]> = {
  header: [
    'Band name (large, bold) + title suffix',
    'Show title',
    'Date / Venue',
    'Status / Type',
  ],
  travel: ['TRAVEL table: From | To | Means of transport (printed only when the date carries travel data)'],
  'general-info': [
    'GENERAL INFORMATION label:value grid (WELCOME, TIME ZONE, ELECTRICITY, WI-FI, …)',
  ],
  venue: [
    'VENUE label:value grid (name, address, city/zip, country, phone, email, website, PARKING, electricity, timezone)',
  ],
  'venue-photo': ['VENUE PHOTO: the venue\'s document photo, full width, with its caption (hidden when the venue has no photo)'],
  'band-crew': ['BAND AND CREW table: Name | Position (who is on the road for this date)'],
  contacts: ['CONTACT PERSONS table: Role | Name | Phone | Email'],
  hotel: ['HOTEL table: Hotel | Address | Phone | Confirmation'],
  rooming: ['ROOMING LIST table: Name | Room | Hotel'],
  'run-of-show': ['TIMINGS table: Time | Activity | Responsible'],
  guestlist: ['GUESTLIST table: Name | Role | Status'],
  flights: ['FLIGHTS / TRANSFERS table: Person | From | To | Flight | Time · Duration'],
  distances: ['DISTANCES table: From | To | Mode | Distance | Duration'],
  notes: ['NOTES free text from the show'],
  'tech-notes': ['TECH NOTES: the show tech notes (one block per note)'],
  'misc-notes': ['LOGISTICS NOTES: category + content per miscellaneous note'],
  'categorized-notes': ['NOTES: category + title + content per categorized note'],
};

/** Default fixed texts — identical to the original hard-coded strings (no
 * title suffix, no footer/header so the original look is unchanged). */
export const DEFAULT_DAYSHEET_LAYOUT_TEXTS: DaysheetLayoutTexts = {
  titleSuffix: '',
  footerLeftTemplate: '',
  footerCenterTemplate: '',
  footerRightTemplate: '',
  headerEnabled: false,
  headerLeftTemplate: '',
  headerCenterTemplate: '',
  headerRightTemplate: '',
};

/** Default style — identical to the original hard-coded pdfmake values. */
export const DEFAULT_DAYSHEET_LAYOUT_STYLE: DaysheetLayoutStyle = {
  titleFontSize: 20,
  titleColor: '#000000',
  subtitleFontSize: 14,
  subtitleColor: '#000000',
  headingFontSize: 12,
  headingColor: '#888888',
  labelFontSize: 9,
  labelColor: '#555555',
  bodyFontSize: 10,
  textColor: '#000000',
  footerFontSize: 8,
  footerColor: '#888888',
  pageMargins: [40, 40, 40, 50],
};

/** Default renderable fields per section. When a section has a fields list it
 * renders those fields in list order; otherwise the section emits its engine
 * block as a single unit. Spacer/text fields can always be added in the editor. */
export const DEFAULT_DAYSHEET_SECTION_FIELDS: Record<DaysheetSectionId, { id: string; label: string }[]> = {
  header: [
    { id: 'header.band', label: 'Band name' },
    { id: 'header.title', label: 'Show title' },
    { id: 'header.date-venue', label: 'Date / Venue' },
    { id: 'header.status-type', label: 'Status / Type' },
  ],
  'general-info': [
    { id: 'general-info.welcome', label: 'Welcome note' },
    { id: 'general-info.timezone', label: 'Time zone' },
    { id: 'general-info.electricity', label: 'Electricity' },
    { id: 'general-info.wifi', label: 'Venue Wi-Fi' },
    { id: 'general-info.currency', label: 'Currency' },
    { id: 'general-info.rate', label: 'Conversion rate' },
    { id: 'general-info.luggage', label: 'Luggage notes' },
    { id: 'general-info.duration', label: 'Show duration' },
    { id: 'general-info.stage', label: 'Stage size' },
    { id: 'general-info.artist-pass', label: 'Artist pass' },
    { id: 'general-info.passport', label: 'Passport reminder' },
  ],
  venue: [
    { id: 'venue.name', label: 'Venue name' },
    { id: 'venue.address', label: 'Address' },
    { id: 'venue.city-zip', label: 'City / ZIP' },
    { id: 'venue.country', label: 'Country' },
    { id: 'venue.phone', label: 'Phone' },
    { id: 'venue.email', label: 'Email' },
    { id: 'venue.website', label: 'Website' },
    { id: 'venue.parking', label: 'Parking' },
    { id: 'venue.electricity', label: 'Electricity' },
    { id: 'venue.timezone', label: 'Timezone' },
  ],
  'venue-photo': [
    { id: 'venue-photo.image', label: 'Venue photo' },
    { id: 'venue-photo.caption', label: 'Caption' },
  ],
  // ⚠️ Les sections TABLEAU n'ont plus d'entrée bloc `<section>.table` : elles
  // exposent une entrée PAR COLONNE (`<section>.<columnId>`), comme le rider.
  // Ordre, visibilité et largeur de chaque colonne vivent donc dans `fields`.
  // Une liste enregistrée SANS aucun id de colonne connu reste rendue avec TOUTES
  // les colonnes (back-compat, cf. `daysheetTableColumns`).
  travel: [],
  'band-crew': [], contacts: [], hotel: [], rooming: [],
  'run-of-show': [], guestlist: [], flights: [], distances: [],
  notes: [{ id: 'notes.text', label: 'Notes text' }],
  'tech-notes': [{ id: 'tech-notes.list', label: 'Tech notes list' }],
  'misc-notes': [{ id: 'misc-notes.list', label: 'Logistics notes list' }],
  'categorized-notes': [{ id: 'categorized-notes.list', label: 'Categorized notes list' }],
};

/** The default layout = EXACT current behaviour of buildDaysheetDoc: all
 * sections enabled in the current render order. */
export function createDefaultDaysheetLayout(): DaysheetLayout {
  return {
    version: 1,
    sections: DEFAULT_SECTION_ORDER.map((id) => ({
      id,
      enabled: true,
      title: null,
      // La photo de salle occupe une page à elle (comme le tour book de Håkon) :
      // saut de page AVANT par défaut, que l'éditeur peut décocher.
      ...(id === 'venue-photo' ? { pageBreakBefore: true } : {}),
      fields: daysheetTableColumnFields(id).length
        ? daysheetTableColumnFields(id)
        : (DEFAULT_DAYSHEET_SECTION_FIELDS[id] || []).map((f) => ({ id: f.id, enabled: true, label: f.label })),
    })),
    texts: { ...DEFAULT_DAYSHEET_LAYOUT_TEXTS },
    style: { ...DEFAULT_DAYSHEET_LAYOUT_STYLE },
  };
}

/**
 * Merge a persisted (partial) layout override over the defaults.
 * - texts/style: shallow-merged per key (override wins, defaults fill gaps).
 * - sections: the override array wins WHOLESALE (it is the full ordered list);
 *   when absent the default sections are kept. Unknown / duplicate section ids
 *   are tolerated (the engine iterates in list order).
 */
export function mergeDaysheetLayouts(
  base: DaysheetLayout,
  override: Partial<DaysheetLayout> | null,
): DaysheetLayout {
  if (!override) return base;
  const defaultFieldsFor = (id: DaysheetSectionId) => {
    const cols = daysheetTableColumnFields(id);
    if (cols.length) return cols; // section TABLEAU : une entrée par colonne
    return (DEFAULT_DAYSHEET_SECTION_FIELDS[id] || []).map((f) => ({ id: f.id, enabled: true, label: f.label }));
  };
  const sections = (override.sections ?? base.sections).map((s) => {
    const sid = s.id as DaysheetSectionId;
    const colIds = (DAYSHEET_TABLE_COLUMNS[sid] || []).map((c) => `${sid}.${c.id}`);
    const isTable = colIds.length > 0;
    // Legacy : un layout enregistré avant les colonnes-champs ne contient que
    // `<section>.table`. Cet id devient une ligne morte dans l'éditeur → on le
    // retire (ses effets sont repris par les colonnes elles-mêmes).
    const rawSaved = Array.isArray(s.fields) && s.fields.length > 0 ? s.fields : null;
    const savedFields = rawSaved
      ? rawSaved.filter((f) => !(isTable && f.id === `${sid}.table`))
      : null;
    const defaults = defaultFieldsFor(sid);
    // Append any NEW default fields missing from the saved list (order kept).
    const fields = savedFields
      ? [...savedFields, ...defaults.filter((d) => !savedFields.some((f) => f.id === d.id))]
      : defaults;
    return { ...s, fields };
  });
  // Back-compat: a layout saved before a NEW section was added to the default
  // order (e.g. 'tech-notes' / 'misc-notes' / 'categorized-notes') won't contain
  // it. Append any default section missing from the saved list, at its DEFAULT
  // position, so the PDF and the Daysheet Layout editor always expose the full
  // section set. Sections already present (matched by id) are never duplicated,
  // and the relative order of saved non-default sections is preserved.
  const presentIds = new Set(sections.map((s) => s.id));
  const defaultSectionById = new Map(
    createDefaultDaysheetLayout().sections.map((d) => [d.id, d]),
  );
  const defaultIndex = new Map(DEFAULT_SECTION_ORDER.map((id, i) => [id, i]));
  // Insert missing default sections into the saved list at their DEFAULT
  // position — before the first saved section whose default index is greater —
  // while preserving the relative order of the saved sections.
  const pendingDefaults = DEFAULT_SECTION_ORDER.filter(
    (id) => defaultSectionById.has(id) && !presentIds.has(id),
  );
  const sectionsWithDefaults: DaysheetLayoutSection[] = [];
  for (const s of sections) {
    const idx = defaultIndex.get(s.id as DaysheetSectionId) ?? Infinity;
    while (
      pendingDefaults.length &&
      (defaultIndex.get(pendingDefaults[0] as DaysheetSectionId) ?? Infinity) < idx
    ) {
      sectionsWithDefaults.push(defaultSectionById.get(pendingDefaults.shift()!)!);
    }
    sectionsWithDefaults.push(s);
  }
  for (const id of pendingDefaults) sectionsWithDefaults.push(defaultSectionById.get(id)!);
  return {
    version: override.version ?? base.version,
    sections: sectionsWithDefaults,
    texts: { ...base.texts, ...(override.texts ?? {}) },
    style: { ...base.style, ...(override.style ?? {}) },
  };
}
